import React from "react"

// Models
import {
    Beat,
    BEAT_CLASSES,
    clearPoint,
    confirm as confirmBeat,
    Cursor,
    orderProblems,
    PointKey,
    POINT_KEYS,
    progress,
    sameJudgement,
    sampleMs,
    setClass,
    setPoint,
    snapToSample,
    startingPoint,
    stepCursor,
    toggleUncertain,
    toggleUsable,
} from "./model"

/**
 * Everything the annotation surface remembers, and the keyboard that drives it.
 *
 * Kept out of the view so the rules — what a nudge does, when a beat counts as
 * reviewed, what undo restores — can be reasoned about and tested without a
 * canvas. The view draws; this decides.
 *
 * The keyboard is the primary instrument rather than a convenience. Even with
 * the work area magnified enough for the mouse to be honest, a technician
 * correcting several thousand boundaries should not have to aim at any of them,
 * and the hands never need to leave these keys.
 */

/**
 * How far one arrow press moves a boundary: exactly one sample at the
 * record's rate, or — with Shift — the whole number of samples nearest 10 ms.
 *
 * `Math.round(1000 / rate)` used to be the fine step, which is 3 ms at
 * 360 Hz: not a sample, so marks drifted off the grid.
 */
export function stepFor(sampleRate: number, coarse: boolean): number {
    const one = sampleMs(sampleRate);

    return coarse ? Math.max(1, Math.round(10 / one)) * one : one
}

/** True while the reviewer is typing somewhere, so we keep our hands off. */
function isTyping(target: EventTarget | null): boolean {
    const node = target as HTMLElement | null;

    if (!node || !node.tagName) {
        return false
    }

    const tag = node.tagName.toLowerCase();

    return tag === "input" || tag === "textarea" || tag === "select" || node.isContentEditable
}

/**
 * True while a modal dialog is open — the approve confirmation, say.
 *
 * These keys listen on the window, so without this they kept working behind
 * the dialog: Enter in the approve dialog also confirmed a beat, and the
 * arrows moved marks nobody could see.
 */
export function modalOpen(): boolean {
    return typeof document !== "undefined" && document.querySelector("[aria-modal=\"true\"]") !== null
}

/**
 * Is the keyboard focus on the annotation surface (or nowhere in particular)?
 *
 * Only Tab asks: taken page-wide it trapped keyboard users, who could no
 * longer Tab to the toolbar or the dialogs. The view marks its root with
 * `data-annotation-surface`.
 */
export function inSurface(target: EventTarget | null): boolean {
    const node = target as HTMLElement | null;

    if (!node || typeof document === "undefined" || node === document.body || node === document.documentElement) {
        return true
    }

    return typeof node.closest === "function" && node.closest("[data-annotation-surface]") !== null
}

export type AnnotationApi = {
    beats: Beat[]
    /** 1-based. */
    current: number
    selected: PointKey
    counts: ReturnType<typeof progress>
    select(beat: number, key: PointKey): void
    move(beat: number, key: PointKey, ms: number | null): void
    /** A drag is one gesture: one undo step however many moves it makes. */
    beginDrag(): void
    endDrag(): void
    setBeats(beats: Beat[]): void
    canUndo: boolean
    /**
     * The labels these beats were last reset from. Equal to what the page
     * handed in once the reset has happened — until then the beats on screen
     * still belong to whatever came before, and must not be saved under the
     * new record.
     */
    loadedFrom: Beat[]
}

type State = {beats: Beat[], cursor: Cursor, from: Beat[]}

const UNDO_DEPTH = 200;

/** Keep what is about to change, for undo — bounded, so a long session cannot grow it forever. */
function remember(stack: Beat[][], beats: Beat[]) {
    stack.push(beats);

    if (stack.length > UNDO_DEPTH) {
        stack.shift()
    }
}

export default function useAnnotation(
    initial: Beat[],
    sampleRate: number,
    options?: {
        enabled?: boolean
        /**
         * The beats as the server has them. A beat edited back to the same
         * judgement takes its status and stamp from here again — moving a
         * mark and back is not a correction. Defaults to `initial`.
         */
        origin?: Beat[]
        /** The record's last sample, in ms: nothing is placed past it. */
        maxMs?: number
    },
): AnnotationApi {
    const rateOk = isFinite(sampleRate) && sampleRate > 0;
    const enabled = (options ? options.enabled !== false : true) && rateOk;
    const origin = (options && options.origin) || initial;
    const maxMs = options ? options.maxMs : undefined;

    /**
     * The beats AND the cursor as one state, moved only by functional updates.
     *
     * They have to travel together. Held apart, an edit read `beats` and the
     * beat index out of the effect's closure while the cursor advanced
     * functionally, so any two keystrokes landing in one render tick used the
     * same stale index: two quick Enters confirmed one beat twice and skipped
     * the next, and an arrow arriving mid-tick nudged a point on the PREVIOUS
     * beat while the screen showed a later one. Silent, and it corrupts the one
     * thing this tool exists to produce.
     *
     * Every mutation below therefore derives from `prev` and never from a
     * captured variable.
     */
    const [state, setState] = React.useState<State>({
        beats: initial,
        cursor: {beat: 1, key: POINT_KEYS[0]},
        from: initial,
    });

    const beats = state.beats;
    const current = state.cursor.beat;
    const selected = state.cursor.key;

    const undoRef = React.useRef<Beat[][]>([]);
    const [canUndo, setCanUndo] = React.useState(false);

    /* A drag in progress: whether its one undo step has been taken. */
    const dragRef = React.useRef<{remembered: boolean} | null>(null);

    /* A new record replaces everything, including the history. */
    React.useEffect(() => {
        setState({beats: initial, cursor: {beat: 1, key: POINT_KEYS[0]}, from: initial});
        undoRef.current = [];
        dragRef.current = null;
        setCanUndo(false)
    }, [initial]);

    /** Keep the undo affordance honest without reading the ref during render. */
    React.useEffect(() => {
        setCanUndo(undoRef.current.length > 0)
    }, [state]);

    /* the latest of these, for the callbacks below without re-binding them */
    const live = React.useRef({sampleRate: sampleRate, maxMs: maxMs, origin: origin});

    live.current = {sampleRate: sampleRate, maxMs: maxMs, origin: origin};

    /**
     * A beat after an edit: back to the server's own beat when it now says
     * exactly what that one says, whatever was done to get here.
     */
    const settle = React.useCallback((beat: Beat): Beat => {
        const original = live.current.origin.filter(b => b.number === beat.number)[0];

        return original && sameJudgement(beat, original) ? original : beat
    }, []);

    /**
     * Apply `fn` to one beat, settle it, and record the change for undo. No
     * change, no undo step and no new state.
     */
    const change = React.useCallback((
        prev: State,
        index: number,
        fn: (beat: Beat) => Beat,
        rememberIt: boolean = true,
    ): State => {
        const target = prev.beats[index];

        if (!target) {
            return prev
        }

        const updated = settle(fn(target));

        if (updated === target) {
            return prev
        }

        if (rememberIt) {
            remember(undoRef.current, prev.beats)
        }

        return Object.assign({}, prev, {beats: prev.beats.map((b, i) => (i === index ? updated : b))})
    }, [settle]);

    /** Apply a change to the beat the cursor is on. `fn` gets the CURRENT beat. */
    const edit = React.useCallback((fn: (beat: Beat) => Beat) => {
        setState(prev => change(prev, prev.cursor.beat - 1, fn))
    }, [change]);

    const select = React.useCallback((beat: number, key: PointKey) => {
        setState(prev => Object.assign({}, prev, {
            cursor: {
                beat: Math.min(Math.max(1, beat), Math.max(1, prev.beats.length)),
                key: key,
            },
        }))
    }, []);

    /**
     * Move a named boundary on a named beat — the pointer's entry point.
     * Snapped to the sample grid and kept inside the record; a move that lands
     * on the same sample changes nothing. During a drag only the first move
     * takes an undo step.
     */
    const move = React.useCallback((beat: number, key: PointKey, ms: number | null) => {
        const {sampleRate: rate, maxMs: top} = live.current;

        if (!(rate > 0)) {
            return
        }

        const to = ms === null ? null : snapToSample(ms, rate, top);
        const drag = dragRef.current;

        setState(prev => {
            const target = prev.beats[beat - 1];

            if (!target || target.points[key] === to) {
                return prev
            }

            const next = change(prev, beat - 1, b => (to === null ? clearPoint(b, key) : setPoint(b, key, to)),
                !drag || !drag.remembered);

            if (drag && next !== prev) {
                drag.remembered = true
            }

            return next
        })
    }, [change]);

    const beginDrag = React.useCallback(() => {
        dragRef.current = {remembered: false}
    }, []);

    const endDrag = React.useCallback(() => {
        dragRef.current = null
    }, []);

    /**
     * Step through the six boundaries in time order, rolling into the next beat
     * at the end — so the whole record is one repeated gesture rather than a
     * decision about where to go next.
     */
    const walk = React.useCallback((step: number) => {
        setState(prev => Object.assign({}, prev, {
            cursor: stepCursor(prev.cursor, step, prev.beats.length),
        }))
    }, []);

    React.useEffect(() => {
        if (!enabled) {
            return
        }

        /*
         * Nothing in here reads a beat, an index or the selection from this
         * closure. Every branch hands a function to the state update, which
         * resolves them against the state as it is at the moment the update
         * runs — so a burst of keystrokes inside one render tick applies in
         * order instead of all landing on whichever beat was current when the
         * listener was bound.
         */
        function onKey(event: KeyboardEvent) {
            if (isTyping(event.target) || modalOpen()) {
                return
            }

            /*
             * Ctrl/Cmd+Z is the one chord here; every other key is a BARE key.
             *
             * With Ctrl, Cmd or Alt held a key belongs to someone else, and
             * these used to fire anyway: Ctrl+Enter opens the approve dialog
             * and ALSO confirmed the current beat and moved on, stamping a
             * review nobody made. Shift stays allowed — Shift+arrows is the
             * coarse nudge, Shift+Tab walks back.
             */
            if (event.ctrlKey || event.metaKey || event.altKey) {
                const undo = (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey
                    && (event.key === "z" || event.key === "Z");

                if (undo) {
                    setState(prev => {
                        const previous = undoRef.current.pop();

                        return previous ? Object.assign({}, prev, {beats: previous}) : prev
                    });
                    event.preventDefault()
                }

                return
            }

            const {sampleRate: rate, maxMs: top} = live.current;
            let handled = true;

            if (event.key === "Tab") {
                /* not a trap: away from the surface, Tab is the page's */
                if (!inSurface(event.target)) {
                    return
                }

                walk(event.shiftKey ? -1 : 1)
            } else if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
                const delta = stepFor(rate, event.shiftKey) * (event.key === "ArrowLeft" ? -1 : 1);

                /* an absent mark is ADDED by the first press, at its starting
                   point; the presses after that nudge it, sample by sample */
                setState(prev => change(prev, prev.cursor.beat - 1, b => {
                    const key = prev.cursor.key;
                    const at = b.points[key];
                    const to = at === null
                        ? startingPoint(b, key, rate, top)
                        : snapToSample(at + delta, rate, top);

                    return to === at ? b : setPoint(b, key, to)
                }))
            } else if (event.key === "Enter") {
                /* confirm AND advance in one update, so holding Enter through a
                   run of good beats confirms each exactly once */
                setState(prev => {
                    const index = prev.cursor.beat - 1;
                    const target = prev.beats[index];

                    if (!target) {
                        return prev
                    }

                    /* a beat that breaks the order is not confirmed, nor left
                       behind: the screen says why (view footer and cells) */
                    if (orderProblems(target, prev.beats[index + 1] || null, prev.beats[index - 1] || null).length) {
                        return prev
                    }

                    remember(undoRef.current, prev.beats);

                    return Object.assign({}, prev, {
                        beats: prev.beats.map((b, i) => (i === index ? confirmBeat(b) : b)),
                        cursor: prev.cursor.beat < prev.beats.length
                            ? {beat: prev.cursor.beat + 1, key: POINT_KEYS[0]}
                            : prev.cursor,
                    })
                })
            } else if (event.key === "Delete" || event.key === "Backspace") {
                setState(prev => change(prev, prev.cursor.beat - 1, b => clearPoint(b, prev.cursor.key)))
            } else if (event.key === "u" || event.key === "U") {
                setState(prev => change(prev, prev.cursor.beat - 1, b => toggleUncertain(b, prev.cursor.key)))
            } else if (event.key === "x" || event.key === "X") {
                edit(toggleUsable)
            } else if (event.key >= "1" && event.key <= "5") {
                const cls = BEAT_CLASSES[Number(event.key) - 1];

                edit(beat => setClass(beat, cls))
            } else {
                handled = false
            }

            if (handled) {
                event.preventDefault()
            }
        }

        window.addEventListener("keydown", onKey);

        return () => window.removeEventListener("keydown", onKey)
    }, [enabled, walk, edit, change]);

    const setBeats = React.useCallback((next: Beat[]) => {
        setState(prev => {
            remember(undoRef.current, prev.beats);

            return Object.assign({}, prev, {beats: next})
        })
    }, []);

    return {
        beats: beats,
        current: current,
        selected: selected,
        counts: progress(beats),
        select: select,
        move: move,
        beginDrag: beginDrag,
        endDrag: endDrag,
        setBeats: setBeats,
        canUndo: canUndo,
        loadedFrom: state.from,
    }
}
